import { Sites } from '@ps/platform-message-framework/common/Sites';
import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { StarsButton, Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useMemo } from 'react';
import { useField, useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import PhoneNumberInput from '@swf/components/PhoneNumberInput/PhoneNumberInput';
import useWizardJump from '@swf/core/hooks/useWizardJump';
import { select } from '@swf/core/store';
import { getCountryCode, getCountryShortName } from '@swf/core/utils/phoneVerification';

import {
  TXTCLI_Continue,
  TXTMOB_WeXll_use_this_for_account_sec_ELL,
  TXTWCL_Number_XverifiedX,
  TXTWCL_Verify_my_number,
  TXTWCL_WeXll_use_this_for_account_sec_ELL,
  TXTWCL_We_need_your_mobile_number_to__ELL
} from './VerifyNumber.messages.json';
import styles from './VerifyNumber.scss';

type SVGProps = React.SVGAttributes<SVGElement>;
interface Props {
  first?: boolean;
  verifyNumber?(): void;
  callingCode?: string;
  phoneVefification?: boolean;
  phoneFormatCountry?: string;
  autoFocus?: boolean;
  fieldName?: string;
  gridLayout?: boolean;
  icon?: React.ReactElement<SVGProps, React.FunctionComponent<SVGProps>>;
}

const getSubTitleText = (site: Sites) => {
  switch (site) {
    case Sites.CA_ON:
    case Sites.USMI:
      return TXTWCL_WeXll_use_this_for_account_sec_ELL;
    case Sites.DE:
      return TXTMOB_WeXll_use_this_for_account_sec_ELL;
    default:
      return TXTWCL_We_need_your_mobile_number_to__ELL;
  }
};

const VerifyNumber: React.FC<React.PropsWithChildren<Props>> = ({
  first = false,
  gridLayout = false,
  verifyNumber,
  children,
  callingCode,
  phoneFormatCountry,
  autoFocus,
  fieldName,
  icon
}) => {
  const {
    input: { value: currentPhoneNumber }
  } = useField('mobilePhoneNumber');

  const site = useSelector(select.cms.getSiteId);
  const swcRMFPhoneVerification = useFeature(FeatureFlag.SwcRMFPhoneVerification);
  const isRMFPhoneVerificationStatus = useFeature(FeatureFlag.SwcRMFPhoneVerificationStatus);
  const isUSPAEnhancedFlow = useFeature(FeatureFlag.SwcUSPAEnhancedFlow);
  const mobileVerifiedAccount = useSelector(select.account.isMobileVerified);
  const mobilePhoneNumber = useSelector(select.account.getMobilePhone);

  const form = useForm();
  const { values, valid } = form.getState();
  const userCountry = useSelector(select.account.getCountry);
  const { jumpForward } = useWizardJump();

  const country = useMemo(() => {
    if (values.dialingCode && values.dialingCode.length < 3) return values.dialingCode;
    if (values.country && values.dialingCode && values.dialingCode.length > 2)
      return getCountryShortName(values.country);
    if (callingCode) return callingCode;
    if (userCountry) return userCountry;
    return values?.country;
  }, [values.dialingCode, values?.country, callingCode, userCountry]);

  const dialCode = getCountryCode(country)?.dialingCode;
  const isSameNumber = mobileVerifiedAccount
    ? `${dialCode}${currentPhoneNumber}` === mobilePhoneNumber
    : false;
  const isVerifiedFlow = swcRMFPhoneVerification || !first;

  const options = useMemo(() => {
    if (!isRMFPhoneVerificationStatus) {
      return { translation: undefined, label: undefined, icon: undefined };
    }

    const translation = !isVerifiedFlow || isSameNumber ? TXTCLI_Continue : TXTWCL_Verify_my_number;
    const showVerifiedLabel = isVerifiedFlow && isSameNumber;

    return {
      translation,
      label: showVerifiedLabel ? TXTWCL_Number_XverifiedX : undefined,
      icon: showVerifiedLabel ? icon : undefined
    };
  }, [isRMFPhoneVerificationStatus, icon, isSameNumber, isVerifiedFlow]);

  const handleClick = isRMFPhoneVerificationStatus
    ? isSameNumber
      ? () => {
          if (!isUSPAEnhancedFlow) {
            jumpForward('#rmf.rmf-legal-name');
          }
        }
      : verifyNumber
    : verifyNumber;

  const translation = isRMFPhoneVerificationStatus
    ? options.translation
    : isVerifiedFlow
    ? TXTWCL_Verify_my_number
    : TXTCLI_Continue;

  return (
    <>
      <p className={styles.paragraph}>
        <Translation {...getSubTitleText(site)} />
      </p>
      <div className={classNames(styles.inputWrapper, verifyNumber && styles.inputColumnWrapper)}>
        {children}
        <PhoneNumberInput
          fieldName={fieldName}
          callingCode={callingCode}
          phoneFormatCountry={country || phoneFormatCountry}
          autoFocus={autoFocus}
          hidePlaceholder={site !== Sites.DE}
          placeholder="xxx-xxx-xxxx"
          label={options?.label}
          icon={options?.icon}
        />
      </div>
      <StarsButton
        block
        onClick={handleClick}
        disabled={first ? false : !valid}
        type={first ? 'submit' : 'button'}
        className={classNames(styles.button, {
          [styles.first]: first,
          [styles.gridLayout]: gridLayout
        })}
        translation={translation}
      />
    </>
  );
};

export default VerifyNumber;
